import { ReactElement, useCallback, useState } from 'react';

import { Timestamp } from 'firebase/firestore';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import dayjs from 'dayjs';

import { useData } from '../../contextProviders/DataProvider';
import useOrg from '../../dataHooks/useOrg';
import useSeasons from '../../dataHooks/useSeasons';
import useEventEditorState from '../../stateHooks/useEventEditorState';
import useGhinIndexDateChange from '../../stateHooks/useGhinIndexDateChange';
import { CoursePair } from '../../types/Course';
import Event from '../../types/Event';
import EventDay from '../../types/EventDay';
import League from '../../types/League';
import { ToDayJS } from '../../utils/utils';

import EventAdvancedSettingsEditor from './EventAdvancedSettingsEditor';
import EventCardPreview from './EventCardPreview';
import EventDateCalendar from './EventDateCalendar';
import EventEssentialsEditor from './EventEssentialsEditor';
import { PreviewElement, EditorSection, FieldTarget, ELEMENT_TO_EDITOR } from './eventPreviewTypes';
import EventSignupTimeEditor from './EventSignupTimeEditor';
import EventTimeEditor from './EventTimeEditor';
import EventTypeEditor from './EventTypeEditor';
import GhinIndexDateChangeConfirm from './GhinIndexDateChangeConfirm';

export interface EventReviewAndFinalizeProps {
  event: Event;
  league?: League;
  isEditing?: boolean;
  isNewEvent?: boolean;
  updateEvent?: (event: Partial<Event>) => Promise<void>;
  onEventChange?: (event: Event) => void;
  onEditingClose?: () => void;
  onEditingOpen?: () => void;
}

export default function EventReviewAndFinalize(props: EventReviewAndFinalizeProps): ReactElement {
  const { getAdminOrgs } = useData();

  const isNewEvent = props.isNewEvent ?? true;

  const { org } = useOrg(props.event.orgId);
  const { seasons } = useSeasons({
    orgId: props.event.orgId || undefined,
    leagueId: props.event.leagueId || undefined,
  });

  const {
    updatedEvent,
    isMultiDay,
    isGolf,
    defaultTime,
    deleteConfirm,
    handleUpdate,
    handleImageUpdated,
    handleFileSelected,
    handleEventDateChange,
    handleEventTimesChange,
    handleLeagueChange,
    handleSeasonChange,
    handleDelete,
    setDeleteConfirm,
    setIsMultiDay,
    handleIsGolfChange,
  } = useEventEditorState({
    event: props.event,
    league: props.league,
    org: org ?? undefined,
    seasons: seasons ?? undefined,
    isNewEvent,
    updateEvent: props.updateEvent,
    onEventChange: props.onEventChange,
  });

  const [expandedSection, setExpandedSection] = useState<EditorSection | false>(false);
  const [focusTarget, setFocusTarget] = useState<FieldTarget | null>(null);

  const handleAccordionChange = (panel: EditorSection) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const { isEditing = false, onEditingClose, onEditingOpen } = props;

  const accordionSx = {
    '&:before': { display: 'none' },
    border: 1,
    borderColor: 'divider',
    '& .MuiAccordionSummary-root': { px: { xs: 1, sm: 2 } },
    '& .MuiAccordionDetails-root': { px: { xs: 1, sm: 2 } },
  };

  // Handle clicks on preview card elements to navigate to the corresponding editor
  const handlePreviewElementClick = useCallback(
    (element: PreviewElement) => {
      const { section, field } = ELEMENT_TO_EDITOR[element];
      setExpandedSection(section);
      setFocusTarget(field ?? null);

      // If not already editing, trigger edit mode
      if (!isEditing && onEditingOpen) {
        onEditingOpen();
      }
    },
    [isEditing, onEditingOpen],
  );

  // Clear focus target after it's been consumed by an editor
  const handleFocusComplete = useCallback(() => {
    setFocusTarget(null);
  }, []);

  const ghinIndexDateChange = useGhinIndexDateChange(props.event, handleUpdate);

  return (
    <Grid container spacing={3} sx={{ pt: 2, alignItems: 'stretch', overflow: 'hidden' }}>
      {/* Preview Card - Full width when not editing, left half when editing on md+ */}
      <Grid
        size={{ xs: 12, md: isEditing ? 5 : 12 }}
        sx={{
          transition: 'all 300ms ease-in-out',
        }}
      >
        <Stack spacing={2}>
          <Fade in timeout={400}>
            <Stack spacing={0.5} sx={{
              alignItems: "flex-start"
            }}>
              <Typography variant="body1" sx={{
                color: "text.secondary"
              }}>
                {isNewEvent
                  ? 'This is how the event will appear to members. Tap on an element to edit.'
                  : 'This is how the event appears to members. Tap on an element to edit.'}
              </Typography>
              {!isEditing && onEditingOpen && (
                <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={onEditingOpen}>
                  Edit
                </Button>
              )}
            </Stack>
          </Fade>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ maxWidth: '27.75em', width: '100%' }}>
              <EventCardPreview
                event={updatedEvent}
                variant="outlined"
                simulateSignupActive
                onElementClick={handlePreviewElementClick}
              />
            </Box>
          </Box>

          {/* "Add" links for missing optional elements */}
          {(!updatedEvent.logo || !updatedEvent.description?.trim()) && (
            <Stack direction="row" spacing={2} sx={{
              justifyContent: "center"
            }}>
              {!updatedEvent.logo && (
                <Link component="button" variant="body2" onClick={() => handlePreviewElementClick('logo')}>
                  + Add logo
                </Link>
              )}
              {!updatedEvent.description?.trim() && (
                <Link component="button" variant="body2" onClick={() => handlePreviewElementClick('description')}>
                  + Add description
                </Link>
              )}
            </Stack>
          )}
        </Stack>
      </Grid>
      {/* Edit Sections - Always rendered for animation, visibility controlled by Slide */}
      <Grid
        size={{ xs: 12, md: isEditing ? 7 : 0 }}
        sx={{
          display: 'flex',
          transition: 'all 300ms ease-in-out',
          overflow: 'hidden',
        }}
      >
        <Slide direction="left" in={isEditing} mountOnEnter unmountOnExit>
          <Box
            sx={{
              borderLeft: { xs: 0, md: 2 },
              borderColor: 'divider',
              pl: { xs: 0, md: 2 },
              flexGrow: 1,
              minWidth: 0,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Stack spacing={1} sx={{ width: '100%', overflow: 'hidden' }}>
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between"
                }}>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Tap a section to make changes.
                </Typography>
                {onEditingClose && (
                  <Button onClick={onEditingClose} size="small">Done</Button>
                )}
              </Stack>

              {/* General Settings */}
              <Accordion
                expanded={expandedSection === 'general'}
                onChange={handleAccordionChange('general')}
                disableGutters
                elevation={0}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">General settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <EventEssentialsEditor
                      event={updatedEvent}
                      isNewEvent={isNewEvent}
                      showOrgNames={getAdminOrgs(true).length > 1}
                      onUpdate={handleUpdate}
                      onLeagueChange={handleLeagueChange}
                      onImageUpdate={handleImageUpdated}
                      onFileSelected={handleFileSelected}
                      focusTarget={expandedSection === 'general' ? focusTarget : null}
                      onFocusComplete={handleFocusComplete}
                    />
                    <EventTypeEditor
                      event={updatedEvent}
                      isGolf={isGolf}
                      onGolfChange={handleIsGolfChange}
                      onUpdate={handleUpdate}
                      focusTarget={expandedSection === 'general' ? focusTarget : null}
                      onFocusComplete={handleFocusComplete}
                    />
                    <EventDateCalendar
                      isMultiDay={isMultiDay}
                      onChange={handleEventDateChange}
                      onMultiDayChange={setIsMultiDay}
                      orientation="portrait"
                      value={Array.from(
                        new Set(
                          updatedEvent.days.map((day) =>
                            ToDayJS(day.datetime as Timestamp, updatedEvent.timezone).format('YYYY-MM-DD'),
                          ),
                        ),
                      ).map((dateStr) => dayjs(dateStr))}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>

              {/* Round Settings */}
              <Accordion
                expanded={expandedSection === 'round'}
                onChange={handleAccordionChange('round')}
                disableGutters
                elevation={0}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Round settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <EventTimeEditor
                    coursePairs={updatedEvent.coursePairs}
                    days={updatedEvent.days}
                    defaultTime={defaultTime}
                    isGolfEvent={isGolf}
                    onChange={(days: EventDay[], coursePairs: CoursePair[]) =>
                      handleEventTimesChange(days, coursePairs)
                    }
                    timezone={updatedEvent.timezone}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Signup Settings */}
              <Accordion
                expanded={expandedSection === 'signup'}
                onChange={handleAccordionChange('signup')}
                disableGutters
                elevation={0}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Signup settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <EventSignupTimeEditor
                      event={updatedEvent}
                      isGolf={isGolf}
                      isTeamLeague={props.league?.isTeam}
                      onUpdate={handleUpdate}
                      onGhinIndexDateChange={ghinIndexDateChange.requestChange}
                      focusTarget={expandedSection === 'signup' ? focusTarget : null}
                      onFocusComplete={handleFocusComplete}
                    />
                    <GhinIndexDateChangeConfirm change={ghinIndexDateChange} />
                  </Stack>
                </AccordionDetails>
              </Accordion>

              {/* Advanced Settings */}
              <Accordion
                expanded={expandedSection === 'advanced'}
                onChange={handleAccordionChange('advanced')}
                disableGutters
                elevation={0}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Advanced settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <EventAdvancedSettingsEditor
                    event={updatedEvent}
                    isGolf={isGolf}
                    isTeamLeague={props.league?.isTeam}
                    isCatchAllLeague={props.league?.isCatchAll}
                    defaultTeamSize={props.league?.defaultTeamSize}
                    includeSignup={false}
                    onUpdate={handleUpdate}
                    onSeasonChange={handleSeasonChange}
                    focusTarget={expandedSection === 'advanced' ? focusTarget : null}
                    onFocusComplete={handleFocusComplete}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Delete action for existing events */}
              {!isNewEvent && (
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    alignItems: "center",
                    pt: 1
                  }}>
                  {!deleteConfirm ? (
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteConfirm(true)}
                    >
                      Delete event
                    </Button>
                  ) : (
                    <>
                      <Button size="small" color="error" onClick={handleDelete}>
                        Confirm Delete
                      </Button>
                      <Button size="small" onClick={() => setDeleteConfirm(false)}>
                        Cancel
                      </Button>
                    </>
                  )}
                </Stack>
              )}
            </Stack>
          </Box>
        </Slide>
      </Grid>
    </Grid>
  );
}
